// T-26 — reine Anzeige-Helfer für einen Treffer der Kontaktsuche.
//
// Gemeinsam genutzt von `KontaktSuchfeld` (Trefferzeile, Chip, Hover-Karte) und
// `TerminKontaktePicker` (gewählte Kontakte), damit beide Stellen gleich
// aussehen: Icon statt Typ-Badge, K/I/S statt Langtext-Status.

import type * as React from "react";
import { FirmaIcon, PersonIcon } from "../../redesign/kvpIcons";
import type {
    KontaktStatusGruppe,
    KontaktTreffer,
    KontaktTrefferFirma,
} from "../../../services/kontaktSucheService";

/**
 * Gruppe des Primär-Status. Bevorzugt die Einteilung des Servers; fehlt sie
 * (ältere Antwort/Fixture), wird sie aus dem Langtext abgeleitet — dieselbe
 * Regel wie im Backend (`kontaktStatusGruppe`): nur „Kunde" und „Interessent"
 * sind eigene Gruppen, alles andere (auch kein Status) ist „Sonstige".
 */
export function kisGruppe(t: Pick<KontaktTreffer, "statusName" | "statusGruppe">): KontaktStatusGruppe {
    if (t.statusGruppe) return t.statusGruppe;
    const s = (t.statusName ?? "").trim();
    if (s === "Kunde") return "kunde";
    if (s === "Interessent") return "interessent";
    return "sonstige";
}

/** Ein Buchstabe für die schmale Pill: K / I / S. */
export function kisKuerzel(g: KontaktStatusGruppe): "K" | "I" | "S" {
    if (g === "kunde") return "K";
    if (g === "interessent") return "I";
    return "S";
}

/** Ausgeschriebene Pill (breite Liste) und `aria-label` der schmalen. */
export function kisLabel(g: KontaktStatusGruppe): string {
    if (g === "kunde") return "Kunde";
    if (g === "interessent") return "Interessent";
    return "Sonstige";
}

/**
 * Firmen-Icon nur für Firmen-Kontakte. Personen — Ansprechpartner wie
 * Privatpersonen — bekommen das Personen-Icon; „Sonstige" ebenfalls, außer der
 * Rohtyp sagt ausdrücklich Firma.
 */
export function trefferIcon(t: Pick<KontaktTreffer, "art" | "contactType">): React.ReactElement {
    return t.art === "firma" || t.contactType === "Firma" ? FirmaIcon : PersonIcon;
}

/** Alle Firmen eines Treffers — mit Rückfall auf das Einzelfeld älterer Antworten. */
export function trefferFirmen(
    t: Pick<KontaktTreffer, "firmen" | "companyId" | "companyName">,
): KontaktTrefferFirma[] {
    if (t.firmen && t.firmen.length > 0) return t.firmen;
    if (t.companyId != null && t.companyName) {
        return [{ id: t.companyId, name: t.companyName, verbindung: null, funktion: null }];
    }
    return [];
}

/**
 * Der Zusatz hinter dem Namen: bei einem Ansprechpartner „bei <erste Firma>",
 * bei einer Privatperson die Anschrift. Firmen und „Sonstige" haben keinen.
 */
export function trefferZusatz(t: KontaktTreffer): string | null {
    if (t.art === "ap") {
        const erste = trefferFirmen(t)[0];
        return erste ? `bei ${erste.name}` : null;
    }
    if (t.art === "privat") return t.anschrift?.trim() || null;
    return null;
}

/** Wie viele Firmen über die erste hinaus — für den „+n"-Hinweis. */
export function weitereFirmen(t: KontaktTreffer): number {
    return t.art === "ap" ? Math.max(trefferFirmen(t).length - 1, 0) : 0;
}

/** Typ im Klartext für die Hover-Karte. */
export function artText(t: Pick<KontaktTreffer, "art">): string {
    if (t.art === "firma") return "Firma";
    if (t.art === "ap") return "Person mit Firmenverbindung";
    if (t.art === "privat") return "Privatperson";
    return "Sonstiger Kontakt";
}
